import { Collapsible } from '@hub/common/Collapsible/Collapsible';
import { useBreakpoints } from '@hub/hooks/useBreakpoints';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { EnvironmentSection } from '../sections/EnvironmentSection';
import { NutritionSection } from '../sections/NutritionSection';
import { ElectricalSection } from '../sections/ElectricalSection';
import { TemperatureSection } from '../sections/TemperatureSection';
import { SurfacePressureSection } from '../sections/SurfacePressureSection';
import { SectionWrapper } from '../sectionWrapper/sectionWrapper';
import { MethodSection } from '../sections/MethodSection';
import { MaterialSection } from '../sections/MaterialSection';
import { v4 } from 'uuid';
import type { ContentItemWithId } from '../../types';
import type { Material, Procedure } from '@igus/iglidur-free-form-hub-interfaces';
import type { UnitSystem } from '@igus/kopla-domain';

export interface FilterFormProps {
  materials: Material[];
  procedure: Procedure[];
  unitSystem: UnitSystem;
  disabled: boolean;
  setActiveIglideStandardModal: (state: boolean) => void;
  activeIglideStandardModal: boolean;
}
export const FilterForm = ({
  disabled,
  materials,
  procedure,
  unitSystem,
  activeIglideStandardModal,
  setActiveIglideStandardModal,
}: FilterFormProps) => {
  const { lg } = useBreakpoints();
  const { t } = useTranslation(['category/productFilter', 'common']);
  const [componentItems, setComponentItems] = useState<ContentItemWithId[]>([]);
  const ComponentToUse = useMemo(() => (lg ? SectionWrapper : Collapsible), [lg]);

  const createComponentItems = () =>
    [
      {
        id: v4(),
        title: lg
          ? t(`HEADLINES.FILTER_PRODUCTS`, { ns: 'free-form-hub/freeFormHub' })
          : t(`HEADLINES.PRODUCTION_METHOD`, { ns: 'free-form-hub/freeFormHub' }),
        content: <MethodSection procedure={procedure} />,
      },
      {
        id: v4(),
        title: lg ? '' : t(`HEADLINES.MATERIAL`, { ns: 'free-form-hub/freeFormHub' }),
        content: (
          <MaterialSection
            materials={materials}
            activeIglideStandardModal={activeIglideStandardModal}
            setActiveIglideStandardModal={setActiveIglideStandardModal}
          />
        ),
      },
      {
        id: v4(),
        title: t(`FILTERS.SECTIONS.TEMPERATURE_RANGE`, { ns: 'free-form-hub/freeFormHub' }),
        content: <TemperatureSection unitSystem={unitSystem} />,
      },
      {
        id: v4(),
        title: t(`FILTERS.SECTIONS.ENVIRONMENT`, { ns: 'free-form-hub/freeFormHub' }),
        content: <EnvironmentSection />,
      },
      {
        id: v4(),
        title: t(`FILTERS.SECTIONS.NUTRITION_SECTOR`, { ns: 'free-form-hub/freeFormHub' }),
        content: <NutritionSection />,
      },
      {
        id: v4(),
        title: t(`FILTERS.SECTIONS.ELECTRICAL_PROPERTIES`, { ns: 'free-form-hub/freeFormHub' }),
        content: <ElectricalSection />,
      },
      {
        id: v4(),
        title: t(`FILTERS.SECTIONS.SURFACE_PRESSURE`, { ns: 'free-form-hub/freeFormHub' }),
        content: <SurfacePressureSection unitSystem={unitSystem} />,
      },
    ].map<ContentItemWithId>(filterCategory => ({
      id: filterCategory.id,
      title: filterCategory.title,
      content: filterCategory.content,
    }));

  useEffect(() => {
    setComponentItems(createComponentItems());
  }, [materials, procedure]);

  return (
    <div className="w-full p-4 bg-gray-50 relative">
      <div className={`absolute ${disabled ? 'opacity-50' : 'hidden'} -left-0 -top-0 z-10 w-full h-full bg-white`} />
      <ComponentToUse items={componentItems} />
    </div>
  );
};
